import React, { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useIBGECities } from '@features/client-management/hooks/useIBGECities';
import {
  chipInputStyle as inputStyle,
  chipInputLabelStyle as labelStyle,
  chipInputChipStyle as chipStyle,
  chipInputRemoveButtonStyle as removeButtonStyle,
} from './chip-input-styles';

interface CityAutocompleteInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  label?: string;
}

const dropdownStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  right: 0,
  zIndex: 50,
  maxHeight: 200,
  overflowY: 'auto',
  background: 'var(--surface-primary)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-md)',
  marginTop: 4,
  boxShadow: 'var(--shadow-md)',
};

const dropdownMessageStyle: CSSProperties = {
  padding: '10px 12px',
  fontSize: 12,
  color: 'var(--text-tertiary)',
};

const suggestionStyle: CSSProperties = {
  display: 'block',
  width: '100%',
  padding: '8px 12px',
  fontSize: 13,
  fontFamily: 'var(--font-family)',
  textAlign: 'left',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: 'var(--text-primary)',
};

interface CitySuggestion {
  id: number | string;
  label: string;
}

interface CityDropdownProps {
  isLoading: boolean;
  suggestions: CitySuggestion[];
  onSelect: (cityLabel: string) => void;
}

/**
 * Dropdown panel listing IBGE city suggestions for the autocomplete input.
 *
 * Renders a loading message while suggestions are being fetched, an
 * empty-state message when none match, or the selectable list otherwise.
 *
 * @param isLoading - Whether the suggestion request is in flight.
 * @param suggestions - City suggestions not already selected.
 * @param onSelect - Callback invoked with the chosen city label.
 * @returns The dropdown content for the current suggestion state.
 */
const CityDropdown: React.FC<CityDropdownProps> = ({ isLoading, suggestions, onSelect }) => {
  const { t } = useTranslation('common');

  if (isLoading) {
    return <div style={dropdownMessageStyle}>{t('inputs.loading')}</div>;
  }
  if (suggestions.length === 0) {
    return <div style={dropdownMessageStyle}>{t('inputs.noCitiesFound')}</div>;
  }
  return (
    <>
      {suggestions.map((city) => (
        <button
          key={city.id}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            onSelect(city.label);
          }}
          style={suggestionStyle}
        >
          {city.label}
        </button>
      ))}
    </>
  );
};

interface SelectedCityChipsProps {
  value: string[];
  onRemove: (city: string) => void;
}

/**
 * Row of removable chips representing the cities currently selected.
 *
 * @param value - The list of selected city labels.
 * @param onRemove - Callback invoked with the city label to remove.
 * @returns The chip list, or null when nothing is selected.
 */
const SelectedCityChips: React.FC<SelectedCityChipsProps> = ({ value, onRemove }) => {
  const { t } = useTranslation('common');

  if (value.length === 0) {
    return null;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
      {value.map((city) => (
        <span key={city} style={chipStyle}>
          {city}
          <button
            type="button"
            onClick={() => onRemove(city)}
            aria-label={t('inputs.removeItem', { item: city })}
            style={removeButtonStyle}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
};

/**
 * Multi-select city input backed by IBGE city suggestions.
 *
 * Users type at least two characters to trigger a debounced suggestion
 * lookup, pick cities from a dropdown, and remove selected cities via chips.
 * Selection state is fully controlled through the {@link CityAutocompleteInputProps.value}
 * and {@link CityAutocompleteInputProps.onChange} props.
 *
 * @param value - The controlled list of selected city labels.
 * @param onChange - Callback invoked with the updated selection.
 * @param placeholder - Placeholder text for the text input.
 * @param label - Optional field label rendered above the input.
 * @returns The city autocomplete control.
 */
export const CityAutocompleteInput: React.FC<CityAutocompleteInputProps> = ({
  value,
  onChange,
  placeholder,
  label,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const { suggestions, isLoading } = useIBGECities(inputValue);

  const filteredSuggestions = suggestions.filter((s) => !value.includes(s.label));

  const handleSelect = (cityLabel: string) => {
    onChange([...value, cityLabel]);
    setInputValue('');
    setShowDropdown(false);
  };

  const handleRemove = (city: string) => {
    onChange(value.filter((v) => v !== city));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setShowDropdown(true);
  };

  const isDropdownVisible = showDropdown && inputValue.length >= 2;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {label && <label style={labelStyle}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          style={inputStyle}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
          placeholder={placeholder}
        />
        {isDropdownVisible && (
          <div style={dropdownStyle}>
            <CityDropdown
              isLoading={isLoading}
              suggestions={filteredSuggestions}
              onSelect={handleSelect}
            />
          </div>
        )}
      </div>
      <SelectedCityChips value={value} onRemove={handleRemove} />
    </div>
  );
};
